import type { Prisma } from "@prisma/client";
import type {
  BuyableItemEvent,
  BuyableItemHandler,
  PartialBuyableItem,
} from "../buyable-item.types";

type PartialRefundBuyableItem = {
  creditNote?: {
    id?: string;
    reference?: string | null;
    creditNoteItems?: Array<{
      orderItem?: {
        finalTotal?: number | null;
        ticket?: {
          code?: string;
          ticketType?: { name?: string; event: BuyableItemEvent } | null;
        } | null;
        badge?: {
          code?: string;
          badgeType?: { name?: string; event: BuyableItemEvent } | null;
        } | null;
      } | null;
    }> | null;
  } | null;
};

type RefundBuyableItem = {
  creditNote: { id: string };
};

type RefundWithEvent = {
  creditNote: {
    creditNoteItems: Array<{
      orderItem: {
        ticket?: {
          code?: string;
          ticketType: { name?: string; event: BuyableItemEvent };
        } | null;
        badge?: {
          code?: string;
          badgeType: { name?: string; event: BuyableItemEvent };
        } | null;
      } | null;
    }>;
  };
};

const GROUP_KEY_PREFIX = "refund";

export const RefundBuyableItemHandler = {
  displayName: "Rachat de titres",
  accounting: {
    generalAccountCode: "706119",
    invoiceLabel: "Rachat de titres",
  },

  getCompleteItemName(item: PartialBuyableItem): string | null {
    const creditNote = (item as unknown as PartialRefundBuyableItem).creditNote;
    const reference = creditNote?.reference;

    if (!reference) return null;

    const creditNoteItems = creditNote?.creditNoteItems ?? [];
    const itemNames = creditNoteItems
      .map((ci) => {
        const ticket = ci.orderItem?.ticket;
        const badge = ci.orderItem?.badge;
        if (ticket?.code) {
          return ticket.code;
        }
        if (badge?.code) {
          return badge.code;
        }
        return null;
      })
      .filter((name): name is string => !!name);

    const itemsDescription =
      itemNames.length > 0 ? ` (${itemNames.join(", ")})` : "";

    return `Remboursement de titres: ${reference}${itemsDescription}`;
  },

  getEvent(item: RefundWithEvent): BuyableItemEvent {
    const creditNoteItems = item.creditNote.creditNoteItems ?? [];
    const event =
      creditNoteItems
        .map(
          (ci) =>
            ci.orderItem?.ticket?.ticketType.event ??
            ci.orderItem?.badge?.badgeType.event
        )
        .find((e): e is BuyableItemEvent => !!e) ?? null;

    if (!event) {
      throw new Error("Refund buyable item has no event");
    }
    return event;
  },

  isValid(item: PartialRefundBuyableItem): boolean {
    return !!item.creditNote?.id;
  },

  findEquivalent(item: PartialRefundBuyableItem): Prisma.BuyableItemWhereInput {
    return { creditNote: { id: item.creditNote?.id } };
  },

  create({
    creditNote,
  }: RefundBuyableItem): Omit<Prisma.BuyableItemCreateInput, "buyableType"> {
    return { creditNote: { connect: { id: creditNote.id } } };
  },

  parseGroupKeyAsWhere(groupKey: string): Prisma.BuyableItemWhereInput {
    if (!RefundBuyableItemHandler.isGroupKey(groupKey)) {
      throw new Error(`Invalid group key for refund: ${groupKey}`);
    }
    const [, creditNoteId] = groupKey.split("|");
    return { creditNote: { id: creditNoteId } };
  },

  isGroupKey(groupKey: string): boolean {
    return groupKey.startsWith(`${GROUP_KEY_PREFIX}|`);
  },

  getGroupKey(item: PartialRefundBuyableItem): string {
    const creditNoteId = item.creditNote?.id;
    if (!creditNoteId) {
      throw new Error("Cannot get group key for invalid refund item");
    }
    return `${GROUP_KEY_PREFIX}|${creditNoteId}`;
  },

  getPricingQuery(item: PartialBuyableItem): Prisma.PricingFindFirstArgs {
    const creditNoteId =
      (item as unknown as PartialRefundBuyableItem).creditNote?.id ??
      (item as unknown as { creditNoteId?: string | null }).creditNoteId ??
      null;

    if (!creditNoteId) {
      throw new Error("Refund buyable item has no creditNoteId");
    }

    return { where: { creditNote: { id: creditNoteId } } };
  },

  getEventSearchFilter(searchText: string): Prisma.BuyableItemWhereInput {
    return {
      creditNote: {
        creditNoteItems: {
          some: {
            orderItem: {
              OR: [
                {
                  ticket: {
                    ticketType: {
                      event: {
                        name: { contains: searchText, mode: "insensitive" },
                      },
                    },
                  },
                },
                {
                  badge: {
                    badgeType: {
                      event: {
                        name: { contains: searchText, mode: "insensitive" },
                      },
                    },
                  },
                },
              ],
            },
          },
        },
      },
    };
  },
} as const satisfies BuyableItemHandler;
